import type { CohortRuleConditions } from "@unbox/api";
import { useMemo } from "react";
import { useCohortRulePreview } from "@/api/cohort-rules";
import { ImpactPreviewBanner } from "@/components/blocklist/rules/impact-preview-banner";
import { useDebounce } from "@/hooks/use-debounce";
import { isConditionComplete } from "../condition-fields";

const isConditionsComplete = (conditions: CohortRuleConditions) =>
  [...conditions.when, ...conditions.except].every(
    (group) => group.conditions.length > 0 && group.conditions.every(isConditionComplete),
  );

interface ImpactPreviewProps {
  conditions: CohortRuleConditions;
}

export function ImpactPreview({ conditions }: ImpactPreviewProps) {
  // Debounce the serialised tree, not the object: useDebounce compares its input by
  // reference, and the tree arrives from react-hook-form's useWatch, which is free to
  // hand back a fresh object each render. Debouncing the reference would restart the
  // timer on every render instead of only when an operator or value actually changes.
  const serialised = JSON.stringify(conditions);
  const debouncedSerialised = useDebounce(serialised, 400);
  const debouncedConditions = useMemo(
    () => JSON.parse(debouncedSerialised) as CohortRuleConditions,
    [debouncedSerialised],
  );

  const preview = useCohortRulePreview(isConditionsComplete(debouncedConditions) ? debouncedConditions : null);

  // Between an edit and the debounce firing, the last result no longer describes the
  // tree on screen. Report that as loading rather than letting a stale count read as current.
  const isStale = serialised !== debouncedSerialised;
  const isLoading = preview.isFetching || isStale;

  return (
    <div className="flex flex-col gap-1.5">
      <ImpactPreviewBanner
        matchCount={preview.data?.matchCount ?? 0}
        totalCount={preview.data?.totalCount ?? 0}
        isLoading={isLoading}
      />
      {!isLoading && preview.data && (
        <p className="px-1 text-xs text-tertiary">
          <span className="font-medium text-secondary">{preview.data.willAssign}</span> will actually be assigned —
          excludes creators already exempt or in another cohort
        </p>
      )}
    </div>
  );
}
